import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Wallet,
  BarChart3,
  MoreHorizontal,
  RefreshCw,
  Download,
  Eye,
  Clock,
  AlertTriangle,
  Shield,
  Activity,
  DollarSign,
  CreditCard,
  ChevronDown,
} from "lucide-react";
import { makeRequest } from "@/utils/makeRequest";
import { useQuery } from "@tanstack/react-query";
import { format } from "timeago.js";
import { formatCurrency } from "@/utils/formatCurrency";
import {
  useAnalytics,
  useAuditLogs,
  useLedgerEntries,
  useMyNotifications,
  useProfile,
  useSystemNotifications,
  useTransactions,
} from "@/hooks";
import Loader from "@/components/loader/Loader";
import { useAppSelector } from "@/store/hooks";
import { ErrorState } from "@/components/ErrorState";
import { formatNumber } from "@/utils/formatNumber";
import type { WalletAsset } from "@/interfaces/asset";
import { formatEvmHumanBalance } from "@/utils";

interface ITransaction {
  id: string;
  type: string;
  amount: string;
  user: string;
  receiver: string;
  status: string;
  time: string;
  method: string;
  tokenSymbol: string;
  tokenName: string;
  txHash?: string;
  walletAsset: WalletAsset;
  isNative: boolean;
}

// Mock data for charts
const volumeData = [
  { name: "00:00", spot: 120000, p2p: 80000, forex: 45000 },
  { name: "04:00", spot: 95000, p2p: 65000, forex: 38000 },
  { name: "08:00", spot: 180000, p2p: 120000, forex: 72000 },
  { name: "12:00", spot: 250000, p2p: 180000, forex: 95000 },
  { name: "16:00", spot: 320000, p2p: 220000, forex: 128000 },
  { name: "20:00", spot: 280000, p2p: 190000, forex: 105000 },
  { name: "23:59", spot: 200000, p2p: 140000, forex: 78000 },
];

const userGrowthData = [
  { name: "Mon", users: 1200, active: 980 },
  { name: "Tue", users: 1350, active: 1100 },
  { name: "Wed", users: 1480, active: 1250 },
  { name: "Thu", users: 1620, active: 1380 },
  { name: "Fri", users: 1850, active: 1580 },
  { name: "Sat", users: 2100, active: 1850 },
  { name: "Sun", users: 1950, active: 1720 },
];

const tokenDistribution = [
  { name: "BTC", value: 35, color: "#F7931A" },
  { name: "ETH", value: 28, color: "#627EEA" },
  { name: "USDT", value: 20, color: "#26A17B" },
  { name: "BNB", value: 10, color: "#F3BA2F" },
  { name: "Others", value: 7, color: "#8884D8" },
];

const pendingKyc = [
  {
    id: "KYC-452",
    name: "Alice Cooper",
    email: "alice@example.com",
    submitted: "2 hours ago",
    level: 2,
    documents: 3,
  },
  {
    id: "KYC-451",
    name: "Bob Martinez",
    email: "bob@example.com",
    submitted: "4 hours ago",
    level: 3,
    documents: 5,
  },
  {
    id: "KYC-450",
    name: "Carol White",
    email: "carol@example.com",
    submitted: "6 hours ago",
    level: 2,
    documents: 3,
  },
];

const activeAlerts = [
  {
    id: 1,
    type: "security",
    message: "Unusual login from new device",
    severity: "high",
    time: "5 min ago",
  },
  {
    id: 2,
    type: "trade",
    message: "Large trade volume detected",
    severity: "medium",
    time: "15 min ago",
  },
  {
    id: 3,
    type: "system",
    message: "API rate limit approaching",
    severity: "low",
    time: "1 hour ago",
  },
];

const statsCards = [
  {
    title: "Total Users",
    value: "45,231",
    change: "+12.5%",
    trend: "up",
    icon: Users,
    color: "from-blue-500 to-cyan-500",
    subtext: "2,340 new this week",
    path: "/admin/users",
  },
  {
    title: "24h Volume",
    value: "$2.4M",
    change: "+8.2%",
    trend: "up",
    icon: BarChart3,
    color: "from-violet-500 to-purple-500",
    subtext: "Across all markets",
    path: "/admin/trades",
  },
  {
    title: "Total AUM",
    value: "$18.7M",
    change: "+15.3%",
    trend: "up",
    icon: Wallet,
    color: "from-emerald-500 to-teal-500",
    subtext: "Assets under management",
    path: "/admin/wallets",
  },
  {
    title: "Active Trades",
    value: "1,247",
    change: "-3.1%",
    trend: "down",
    icon: Activity,
    color: "from-amber-500 to-orange-500",
    subtext: "Currently in progress",
    path: "/admin/trades",
  },
];

function StatCard({ stat }: { stat: (typeof statsCards)[0] }) {
  const Icon = stat.icon;
  const navigate = useNavigate();

  return (
    <Card
      className="relative overflow-hidden group hover:shadow-lg transition-all duration-300 cursor-pointer"
      onClick={() => navigate(stat.path)}
    >
      <div
        className={cn(
          "absolute top-0 right-0 w-24 h-24 opacity-10 rounded-full -translate-y-1/2 translate-x-1/2 bg-gradient-to-br",
          stat.color,
        )}
      />
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {stat.title}
        </CardTitle>
        <div className={cn("p-2 rounded-lg bg-gradient-to-br", stat.color)}>
          <Icon className="h-4 w-4 text-foreground" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{stat.value}</span>
          <span
            className={cn(
              "text-xs font-medium flex items-center",
              stat.trend === "up" ? "text-emerald-500" : "text-red-500",
            )}
          >
            {stat.trend === "up" ? (
              <ArrowUpRight className="h-3 w-3 mr-0.5" />
            ) : (
              <ArrowDownRight className="h-3 w-3 mr-0.5" />
            )}
            {stat.change}
          </span>
        </div>
        <p className="text-xs text-muted-foreground mt-1">{stat.subtext}</p>
      </CardContent>
    </Card>
  );
}

function ActivityFeed({ logs }: { logs: any }) {
  const navigate = useNavigate();
  const activities = [
    {
      id: 1,
      user: "John Doe",
      action: "completed KYC Level 2",
      time: "2 min ago",
      type: "kyc",
    },
    {
      id: 2,
      user: "Sarah Smith",
      action: "deposited $5,000",
      time: "5 min ago",
      type: "deposit",
    },
    {
      id: 3,
      user: "Mike Johnson",
      action: "executed trade worth $12,000",
      time: "12 min ago",
      type: "trade",
    },
    {
      id: 4,
      user: "System",
      action: "performed daily backup",
      time: "15 min ago",
      type: "system",
    },
    {
      id: 5,
      user: "Emma Wilson",
      action: "opened support ticket",
      time: "20 min ago",
      type: "support",
    },
  ];

  const getIcon = (type: string) => {
    switch (type) {
      // case "kyc":
      case "AUTH":
        return <Shield className="h-4 w-4 text-blue-500" />;
      case "deposit":
        return <DollarSign className="h-4 w-4 text-emerald-500" />;
      case "trade":
        return <BarChart3 className="h-4 w-4 text-violet-500" />;
      case "system":
        return <Activity className="h-4 w-4 text-amber-500" />;
      case "support":
        return <CreditCard className="h-4 w-4 text-cyan-500" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-lg">Recent Activity</CardTitle>
          <CardDescription>Latest platform activities</CardDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/logs")}
        >
          View All
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {logs?.data?.slice(0, 5)?.map((activity: any) => (
            <div
              key={activity.id}
              className="flex items-start gap-3 cursor-pointer hover:bg-muted/50 p-2 rounded-lg transition-colors"
            >
              <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                {getIcon(activity.entityType)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm">
                  <span className="font-medium">{activity.description}</span>{" "}
                  {/* {activity.action} */}
                </p>
                <p className="text-xs text-muted-foreground">
                  {format(activity.createdAt)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState("24h");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const currentPage = 1;

  const {
    data: auditLogs,
    isLoading: isLoadingLogs,
    refetch,
  } = useAuditLogs({
    page: currentPage,
    search: "",
    level: "all",
    category: "all",
    dateFilter: "all",
  });

  const { data: analytics } = useAnalytics("30");

  console.log("ANALYTICS", analytics);

  const { user } = useAppSelector((state) => state.auth);

  const { data: notifs } = useMyNotifications(user?.id as string);

  const {
    data: transactions,
    isLoading,
    isError,
    refetch: transactionRefetch,
  } = useTransactions();

  const { data: systemNotif } = useSystemNotifications();
  const { data: entries, isLoading: isLoadingEntries } = useLedgerEntries();

  console.log("CREDIT", formatNumber(entries?.totals.CREDIT));
  console.log(
    "DEBIT",
    formatNumber(entries?.totals.DEBIT + entries?.totals.RESERVE),
  );

  console.log("TRANSACTIONS", transactions);
  console.log("SYSTEM NOTIFS", systemNotif);

  const fetchSystemWallets = async () => {
    const res = await makeRequest.get("/wallet/system/all");

    return res.data;
  };

  const el = document.getElementById("recent-transactions");

  if (el) {
    el.scrollIntoView({ behavior: "smooth" });
  }

  const { data: systemWallets, isLoading: isLoadingSystemWallets } = useQuery({
    queryKey: ["system-wallets"],
    queryFn: fetchSystemWallets,
  });

  const structuredTransactions: ITransaction[] =
    transactions?.data?.map((tx: any) => ({
      id: tx.id,
      type: tx.type || tx.action,
      amount: tx.amount,
      user: `${tx.user?.firstName || ""} ${tx.user?.lastName || ""}`.trim(),
      receiver:
        `${tx.receiver?.firstName || ""} ${tx.receiver?.lastName || ""}`.trim(),
      status: tx.status?.toUpperCase(),
      time: tx.updatedAt,
      method: tx.source,
      tokenSymbol: tx.asset.tokenSymbol,
      tokenName: tx?.asset?.currency,
      txHash: tx.txHash,
      isNative: tx?.asset?.token?.isNative,

      // ✅ ADD THIS (fixes your error)
      walletAsset: tx.walletAsset,
    })) ?? [];
  // .slice(0, 5);

  const handleRefresh = () => {
    setIsRefreshing(true);
    transactionRefetch();
    setTimeout(() => {
      setIsRefreshing(false);
      toast({
        title: "Dashboard refreshed",
        description: "All data has been updated.",
      });
    }, 1000);
  };

  const handleExport = () => {
    toast({
      title: "Export started",
      description: "Your report is being generated.",
    });
  };

  const handleTransactionAction = (action: string, txId: string) => {
    navigate(`/admin/transactions/${txId}`);
  };

  const handleKycAction = (action: string, kycId: string) => {
    toast({
      title: `${action} KYC`,
      description: `KYC ${kycId} has been ${action.toLowerCase()}.`,
    });
  };

  function getStatusColor(status: string) {
    switch (status?.toLowerCase()) {
      case "completed":
        return "bg-emerald-500/20 text-emerald-500";
      case "pending":
        return "bg-amber-500/20 text-amber-500";
      case "processing":
        return "bg-blue-500/20 text-blue-500";
      case "failed":
        return "bg-red-500/20 text-red-500";
      case "cancelled":
        return "bg-rose-500/20 text-rose-500";
      case "reversed":
        return "bg-purple-500/20 text-purple-500";
      default:
        return "bg-gray-500/20 text-gray-500";
    }
  }

  function getTypeColor(type: string) {
    switch (type?.toLowerCase()) {
      case "deposit":
        return "border-emerald-500/50 text-emerald-500";

      case "withdrawal":
        return "border-amber-500/50 text-amber-500";

      case "transfer":
        return "border-blue-500/50 text-blue-500";

      case "swap":
        return "border-indigo-500/50 text-indigo-500";

      case "trade":
        return "border-violet-500/50 text-violet-500";

      case "mint":
        return "border-teal-500/50 text-teal-500";

      default:
        return "border-gray-400/50 text-gray-400";
    }
  }

  if (isLoading || isLoadingEntries || isLoadingLogs) {
    return <Loader type="bounce" />;
  }

  if (isError)
    return (
      <ErrorState
        onRetry={() => {
          void refetch();
          void transactionRefetch();
        }}
      />
    );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Overview of platform performance and key metrics
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Quick Action Buttons */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/wallets")}
          >
            <DollarSign className="mr-2 h-4 w-4" />
            Deposit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/wallets")}
          >
            <ArrowUpRight className="mr-2 h-4 w-4" />
            Withdraw
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/trades")}
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Trade
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/disputes")}
          >
            <Users className="mr-2 h-4 w-4" />
            P2P
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Clock className="mr-2 h-4 w-4" />
                Last {timeRange}
                <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setTimeRange("24h")}>
                Last 24 hours
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeRange("7d")}>
                Last 7 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeRange("30d")}>
                Last 30 days
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeRange("90d")}>
                Last 90 days
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw
              className={cn("h-4 w-4", isRefreshing && "animate-spin")}
            />
          </Button>
          <Button variant="outline" size="icon" onClick={handleExport}>
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => (
          <StatCard key={stat.title} stat={stat} />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Trading Volume Chart */}
        <Card className="lg:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Trading Volume</CardTitle>
              <CardDescription>Volume across all markets</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-violet-500/10"
                onClick={() => navigate("/admin/trades")}
              >
                <div className="h-2 w-2 rounded-full bg-violet-500 mr-1" />
                Spot
              </Badge>
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-emerald-500/10"
                onClick={() => navigate("/admin/disputes")}
              >
                <div className="h-2 w-2 rounded-full bg-emerald-500 mr-1" />
                P2P
              </Badge>
              <Badge
                variant="outline"
                className="text-xs cursor-pointer hover:bg-amber-500/10"
                onClick={() => navigate("/admin/tokens")}
              >
                <div className="h-2 w-2 rounded-full bg-amber-500 mr-1" />
                Forex
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={volumeData}>
                <defs>
                  <linearGradient id="colorSpot" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorP2p" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorForex" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.2}
                />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                <YAxis
                  stroke="#6b7280"
                  fontSize={12}
                  tickFormatter={(v) => `$${v / 1000}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number) => [
                    `$${value.toLocaleString()}`,
                    "",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="spot"
                  stroke="#8b5cf6"
                  fillOpacity={1}
                  fill="url(#colorSpot)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="p2p"
                  stroke="#10b981"
                  fillOpacity={1}
                  fill="url(#colorP2p)"
                  strokeWidth={2}
                />
                <Area
                  type="monotone"
                  dataKey="forex"
                  stroke="#f59e0b"
                  fillOpacity={1}
                  fill="url(#colorForex)"
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Activity Feed */}
        <ActivityFeed logs={auditLogs} />
      </div>

      {/* Second Row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* User Growth */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-all"
          onClick={() => navigate("/admin/analytics")}
        >
          <CardHeader>
            <CardTitle className="text-lg">User Growth</CardTitle>
            <CardDescription>New registrations vs active users</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={userGrowthData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  opacity={0.2}
                />
                <XAxis dataKey="name" stroke="#6b7280" fontSize={12} />
                <YAxis stroke="#6b7280" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                />
                <Bar dataKey="users" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                <Bar dataKey="active" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Token Distribution */}
        <Card
          className="cursor-pointer hover:shadow-lg transition-all"
          onClick={() => navigate("/admin/tokens")}
        >
          <CardHeader>
            <CardTitle className="text-lg">Token Distribution</CardTitle>
            <CardDescription>By trading volume</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={tokenDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                >
                  {tokenDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#1f2937",
                    border: "none",
                    borderRadius: "8px",
                  }}
                  formatter={(value: number) => [`${value}%`, ""]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-2 justify-center mt-2">
              {tokenDistribution.map((token) => (
                <div
                  key={token.name}
                  className="flex items-center gap-1 cursor-pointer hover:opacity-80"
                  onClick={() => navigate("/admin/tokens")}
                >
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: token.color }}
                  />
                  <span className="text-xs text-muted-foreground">
                    {token.name}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Active Alerts */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Active Alerts</CardTitle>
              <CardDescription>Requires attention</CardDescription>
            </div>
            <Badge
              variant="secondary"
              className="bg-red-500/20 text-red-400 cursor-pointer"
              onClick={() => navigate("/admin/logs")}
            >
              {activeAlerts.length}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeAlerts.map((alert) => (
                <div
                  key={alert.id}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:opacity-80 transition-opacity",
                    alert.severity === "high"
                      ? "bg-red-500/5 border-red-500/20"
                      : alert.severity === "medium"
                        ? "bg-amber-500/5 border-amber-500/20"
                        : "bg-blue-500/5 border-blue-500/20",
                  )}
                  onClick={() => navigate("/admin/logs")}
                >
                  <AlertTriangle
                    className={cn(
                      "h-4 w-4 flex-shrink-0 mt-0.5",
                      alert.severity === "high"
                        ? "text-red-500"
                        : alert.severity === "medium"
                          ? "text-amber-500"
                          : "text-blue-500",
                    )}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{alert.message}</p>
                    <p className="text-xs text-muted-foreground">
                      {alert.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Row */}
      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions">Recent Transactions</TabsTrigger>
          <TabsTrigger value="kyc">Pending KYC</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions">
          <Card id="recent-transactions">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Recent Transactions</CardTitle>
                <CardDescription>Latest platform transactions</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/transactions")}
              >
                View All
              </Button>
            </CardHeader>
            <CardContent>
              {/* MOBILE VIEW */}
              <div className="block lg:hidden space-y-3">
                {structuredTransactions?.map((tx, i) => (
                  <div
                    key={tx.id}
                    className="rounded-xl border bg-card p-4 space-y-3"
                  >
                    <div className="flex items-center justify-between">
                      <Badge
                        variant="outline"
                        className={cn(
                          "capitalize text-xs",
                          getTypeColor(tx.type),
                        )}
                      >
                        {tx.type}
                      </Badge>

                      <Badge className={getStatusColor(tx.status)}>
                        {tx.status}
                      </Badge>
                    </div>

                    <div>
                      <p className="text-sm font-medium">
                        {tx.tokenSymbol} {formatCurrency(tx.amount)}
                      </p>

                      <p className="text-xs text-muted-foreground mt-1">
                        {tx.tokenName}
                      </p>
                    </div>

                    <div className="flex flex-col gap-3 text-sm">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-muted-foreground text-xs">
                            Sender
                          </p>

                          <p className="truncate">{tx.user || "-"}</p>
                        </div>

                        <div className="">
                          <p className="text-muted-foreground text-xs">
                            Receiver
                          </p>

                          <p className="truncate">{tx.receiver || "-"}</p>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-muted-foreground text-xs">
                            Method
                          </p>

                          <p>{tx.method}</p>
                        </div>

                        <div>
                          <p className="text-muted-foreground text-xs">Time</p>

                          <p>{format(tx.time)}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      className="w-full"
                      variant="outline"
                      onClick={() => navigate(`/admin/transactions/${tx.id}`)}
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Transaction
                    </Button>
                  </div>
                ))}

                {!structuredTransactions.length && (
                  <div className="text-center py-10 text-muted-foreground">
                    No transactions found
                  </div>
                )}
              </div>

              {/* DESKTOP TABLE */}
              <div className="hidden lg:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">#</th>
                      <th className="text-left p-3">Sender</th>
                      <th className="text-left p-3">Receiver</th>
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Token</th>
                      <th className="text-left p-3">Amount</th>
                      <th className="text-left p-3">Method</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3">Time</th>
                      <th className="text-left p-3">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {structuredTransactions.map((tx, i) => (
                      <tr
                        key={tx.id}
                        className="border-b hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm">
                          {i + 1}.
                          {/* {((meta?.page || 1) - 1) * limit + i + 1} */}
                        </td>

                        <td className="p-3 text-sm">{tx.user}</td>

                        <td className="p-3 text-sm">{tx.receiver}</td>

                        <td className="p-3">
                          <Badge
                            variant="outline"
                            className={cn(
                              "capitalize text-xs",
                              getTypeColor(tx.type),
                            )}
                          >
                            {tx.type}
                          </Badge>
                        </td>

                        <td className="p-3 text-sm">{tx.tokenName}</td>

                        <td className="p-3 text-sm">
                          {tx.tokenSymbol}{" "}
                          {formatEvmHumanBalance(tx.amount, tx.isNative)}
                        </td>

                        <td className="p-3 text-sm text-muted-foreground">
                          {tx.method}
                        </td>

                        <td className="p-3">
                          <Badge className={getStatusColor(tx.status)}>
                            {tx.status}
                          </Badge>
                        </td>

                        <td className="p-3 text-sm text-muted-foreground">
                          {format(tx.time)}
                        </td>

                        <td className="p-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() =>
                              navigate(`/admin/transactions/${tx.id}`)
                            }
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}

                    {!structuredTransactions.length && (
                      <tr>
                        <td
                          colSpan={10}
                          className="text-center py-10 text-muted-foreground"
                        >
                          No transactions found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kyc">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  Pending KYC Verifications
                </CardTitle>
                <CardDescription>Users awaiting verification</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/admin/compliance")}
              >
                View All
              </Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        ID
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Name
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Email
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Level
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Documents
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Submitted
                      </th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingKyc.map((kyc) => (
                      <tr key={kyc.id} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4 text-sm font-medium">
                          {kyc.id}
                        </td>
                        <td className="py-3 px-4 text-sm">{kyc.name}</td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {kyc.email}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">Level {kyc.level}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm">
                          {kyc.documents} docs
                        </td>
                        <td className="py-3 px-4 text-sm text-muted-foreground">
                          {kyc.submitted}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs"
                              onClick={() => handleKycAction("Review", kyc.id)}
                            >
                              Review
                            </Button>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                >
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleKycAction("Approve", kyc.id)
                                  }
                                >
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() =>
                                    handleKycAction("Request Info", kyc.id)
                                  }
                                >
                                  Request More Info
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-red-500"
                                  onClick={() =>
                                    handleKycAction("Reject", kyc.id)
                                  }
                                >
                                  Reject
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
